import React, { useContext, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks";
import {
  UpdateFieldPayload,
  changePatient,
  changePatientData,
  selectPatient,
} from "../../features/patient/patientSlice";
import { Patient } from "../../interfaces/Patient";
import { Select } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { generateOtp, getPatient, getPatients, getPatientPhotoPresignedUrl, uploadPatientPhoto, verifyOtp } from "../../apis/patient";
import { LanguageContext } from "../../providers/LanguageProvider";
import { getReferredDoctors } from "../../apis/referredDoctor";
import Modal from "../../components/Modal";
import AddReferredDoctor from "../AddReferredDoctor";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertFromRaw, convertToRaw } from "draft-js";
import { useNotification } from "../../NotificationContext";

const BasicProfile: React.FC<{ errors: Patient | null }> = ({ errors }) => {
  const [isConsentPopupVisible, setIsConsentPopupVisible] = useState(false);
  const { translate: t } = useContext(LanguageContext);
  const patient = useAppSelector(selectPatient);
  const [doctors, setDoctors] = useState<Array<any>>([]);
  const [isReferredDopctorModalOpen, setReferredDoctorModalOpen] =
    useState<any>();
  const [isVisibleOtpField, setOtpFieldVisible] = useState<boolean>(false);
  const [otp, setOtp] = useState<any>();
  const [validationErrors, setValidationErrors] = useState<any>([]);
  const [guardianSearch, setGuardianSearch] = useState("");
  const [guardianOptions, setGuardianOptions] = useState<Array<any>>([]);
  const [guardianSearching, setGuardianSearching] = useState(false);
  const guardianSearchTimer = useRef<any>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [localPhotoPreview, setLocalPhotoPreview] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  const params = useParams();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const genders = [
    { id: "male", title: "Male" },
    { id: "female", title: "Female" },
    { id: "other", title: "Other" },
  ];

  const bloodGroups = [
    {
      label: "A+",
      value: "A+",
    },
    {
      label: "A-",
      value: "A-",
    },
    {
      label: "B+",
      value: "B+",
    },
    {
      label: "B-",
      value: "B-",
    },
    {
      label: "AB+",
      value: "AB+",
    },
    {
      label: "AB-",
      value: "AB-",
    },
    {
      label: "O+",
      value: "O+",
    },
    {
      label: "O-",
      value: "O-",
    },
    {
      label: "Don't know",
      value: "Dont know",
    },
  ];

  const [commentsEditorState, setCommentsEditorState] = useState(
    EditorState.createEmpty()
  );

  const updateField = (fieldName: keyof Patient, value: string | number | null) => {
    const payload: UpdateFieldPayload = { fieldName, value };
    dispatch(changePatientData(payload));
  };

  useEffect(() => {
    if (params && params.patientId) {
      getPatient(params.patientId).then(async (res: any) => {
        const response = res.data.data;

        if (response?.comments) {
          convertTextToEditor(response.comments, setCommentsEditorState);
        }
        dispatch(changePatient(res.data.data));

        // Refresh photo viapresign when path exists but URL missing/expired
        if (response?.photo && !response?.photoUrl) {
          try {
            const signed: any = await getPatientPhotoPresignedUrl(response.photo);
            const url = signed?.data?.photoUrl || signed?.data?.url || signed?.data?.photo_url;
            if (url) {
              dispatch(changePatientData({ fieldName: "photoUrl", value: url }));
            }
          } catch (e) {
            console.error(e);
          }
        }
      });
    }
  }, [dispatch, params]);

  useEffect(() => {
    fetchDoctors();
  }, []);

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showNotification("error", "Please select an image file", "", "topRight");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showNotification("error", "Photo must be under 5 MB", "", "topRight");
      return;
    }

    if (localPhotoPreview) URL.revokeObjectURL(localPhotoPreview);
    setLocalPhotoPreview(URL.createObjectURL(file));
    setPhotoUploading(true);
    try {
      const res: any = await uploadPatientPhoto(file);
      const data = res?.data || res;
      const path = data.path || data.photo;
      const url = data.photoUrl || data.photo_url;
      if (path) updateField("photo", path);
      if (url) updateField("photoUrl", url);
      showNotification("success", "Photo uploaded", "", "topRight");
    } catch (err: any) {
      showNotification(
        "error",
        "Failed to upload photo",
        err?.response?.data?.error?.details || err?.message || "",
        "topRight"
      );
      setLocalPhotoPreview(null);
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handleRemovePhoto = () => {
    if (localPhotoPreview) URL.revokeObjectURL(localPhotoPreview);
    setLocalPhotoPreview(null);
    updateField("photo", null);
    updateField("photoUrl", null);
  };

  const displayPhotoUrl = localPhotoPreview || patient.photoUrl || null;

  const onBloodGroupChange = (value: string) => {
    updateField("bloodGroup", value);
  };

  const calculateAge = (birthDate: any) => {
    const today = new Date();
    const birthDateObj = new Date(birthDate);
    let age = today.getFullYear() - birthDateObj.getFullYear();
    const monthDifference = today.getMonth() - birthDateObj.getMonth();
    if (
      monthDifference < 0 ||
      (monthDifference === 0 && today.getDate() < birthDateObj.getDate())
    ) {
      age--;
    }
    return age;
  };

  const handleAge = (value: any) => {
    const age = calculateAge(value);
    updateField("birthDate", value);
    updateField("age", age);
  };

  const fetchDoctors = () => {
    getReferredDoctors().then((res: any) => {
      setDoctors(res.data);
    });
  };

  const handleReferredDoctorChange = (value: any) => {
    updateField("referredBy", value);
  };

  const isMinor = patient.age !== "" && patient.age !== null && Number(patient.age) < 18;

  const handleGuardianSearch = (value: string) => {
    setGuardianSearch(value);
    clearTimeout(guardianSearchTimer.current);
    if (!value || value.length < 3) {
      setGuardianOptions([]);
      return;
    }
    guardianSearchTimer.current = setTimeout(() => {
      setGuardianSearching(true);
      getPatients({ phone: value, per_page: 10 })
        .then((res: any) => {
          setGuardianOptions(
            (res.data.data ?? []).filter((p: any) => p.id !== patient.id)
          );
        })
        .finally(() => setGuardianSearching(false));
    }, 400);
  };

  const handleGuardianSelect = (guardianId: number | null) => {
    const selected = guardianOptions.find((p: any) => p.id === guardianId) ?? null;
    updateField("guardianId", guardianId as any);
    dispatch(changePatientData({ fieldName: "guardian", value: selected as any }));
  };

  const handleGuardianClear = () => {
    updateField("guardianId", null as any);
    dispatch(changePatientData({ fieldName: "guardian", value: null as any }));
    setGuardianSearch("");
    setGuardianOptions([]);
  };

  const handleCloseModal = () => {
    setReferredDoctorModalOpen(false);
    fetchDoctors();
  };

  const onEditorStateChange = (
    editorState: EditorState,
    field: keyof Patient,
    setState: React.Dispatch<React.SetStateAction<EditorState>>
  ) => {
    setState(editorState);
    const contentState = editorState.getCurrentContent();
    const contentStateString = JSON.stringify(convertToRaw(contentState));
    updateField(field, contentStateString);
  };

  const convertTextToEditor = (
    text: any,
    setState: React.Dispatch<React.SetStateAction<EditorState>>
  ) => {
    if (text) {
      const contentState = convertFromRaw(JSON.parse(text));
      const initialEditorState = EditorState.createWithContent(contentState);
      setState(initialEditorState);
    }
  };

  const validateConsentForm = () => {
    const validationErrors: any = {};

    if (otp === "") {
      validationErrors.otp = "OTP is required";
    }
    if (patient.phone === "" || patient.phone === null) {
      validationErrors.phone = "Phone is required";
    }
    const hasErrors = Boolean(Object.keys(validationErrors).length);

    return { hasErrors, validationErrors };
  };

  const handleSendOtp = () => {
    const { hasErrors, validationErrors } = validateConsentForm();
    if (hasErrors) {
      setValidationErrors(validationErrors);
      return;
    }
    setValidationErrors(null);
    generateOtp({ phone: patient.phone, type: "consent" }).then(() => {
      showNotification("success", "Otp sent successfully", "", "topRight");
      setOtpFieldVisible(true);
    });
  };

  const handleVerifyOtp = () => {
    const { hasErrors, validationErrors } = validateConsentForm();
    if (hasErrors) {
      setValidationErrors(validationErrors);
      return;
    }
    setValidationErrors(null);

    verifyOtp({
      phone: patient.phone,
      otp,
      type: "consent",
    })
      .then(() => {
        setOtpFieldVisible(false);
        setIsConsentPopupVisible(false);
        showNotification(
          "success",
          "You got the patient's consent",
          "",
          "topRight"
        );
        navigate("/patients");
      })
      .catch((err: any) => {
        showNotification("error", err.response.data.message, "", "topRight");
      });
  };

  const inputCls =
    "block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-primary focus:ring focus:ring-primary/20 sm:text-sm";

  const Section = ({
    title,
    children,
  }: {
    title: string;
    children: React.ReactNode;
  }) => (
    <section className="space-y-3">
      <div className="flex items-center gap-3">
        <h3 className="text-sm font-semibold text-gray-900 whitespace-nowrap">
          {title}
        </h3>
        <div className="h-px flex-1 bg-gray-200" />
      </div>
      {children}
    </section>
  );

  return (
    <div className="bg-white">
      <div className="flex flex-col gap-6 p-4 sm:p-6 max-w-5xl">
        {/* Photo + identity strip */}
        <Section title="Identity">
          <div className="flex flex-col sm:flex-row gap-6">
            <div className="flex flex-col items-center sm:items-start gap-2 shrink-0">
              <div className="h-24 w-24 rounded-full overflow-hidden bg-gray-100 border border-gray-200 flex items-center justify-center">
                {displayPhotoUrl ? (
                  <img
                    src={displayPhotoUrl}
                    alt="Patient"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="text-xs text-gray-400">No photo</span>
                )}
              </div>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                className="hidden"
                onChange={handlePhotoSelect}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={photoUploading}
                  onClick={() => photoInputRef.current?.click()}
                  className="px-2.5 py-1 text-xs font-medium text-white rounded-md bg-primary hover:bg-hover disabled:opacity-60"
                >
                  {photoUploading
                    ? "Uploading…"
                    : displayPhotoUrl
                    ? "Change"
                    : "Upload"}
                </button>
                {displayPhotoUrl && (
                  <button
                    type="button"
                    disabled={photoUploading}
                    onClick={handleRemovePhoto}
                    className="px-2.5 py-1 text-xs font-medium text-gray-600 rounded-md border border-gray-300 hover:bg-gray-50"
                  >
                    Remove
                  </button>
                )}
              </div>
              <p className="text-[11px] text-gray-400">JPEG / PNG · max 5 MB</p>
            </div>

            <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  {t("first_name")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`${inputCls} capitalize`}
                  name="firstName"
                  value={patient.firstName}
                  onChange={(e) => {
                    if (/^[a-zA-Z]*$/.test(e.target.value)) {
                      updateField("firstName", e.target.value);
                    }
                  }}
                />
                {errors?.firstName && (
                  <span className="mt-1 text-xs text-red-600">{errors.firstName}</span>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  {t("middle_name")}
                </label>
                <input
                  type="text"
                  className={`${inputCls} capitalize`}
                  value={patient.middleName}
                  onChange={(e) => {
                    if (/^[a-zA-Z]*$/.test(e.target.value)) {
                      updateField("middleName", e.target.value);
                    }
                  }}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  {t("last_name")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className={`${inputCls} capitalize`}
                  value={patient.lastName}
                  onChange={(e) => {
                    if (/^[a-zA-Z]*$/.test(e.target.value)) {
                      updateField("lastName", e.target.value);
                    }
                  }}
                />
                {errors?.lastName && (
                  <span className="mt-1 text-xs text-red-600">{errors.lastName}</span>
                )}
              </div>

              <div className="sm:col-span-3">
                <label className="text-sm font-medium text-gray-700">
                  {t("gender")}
                </label>
                <div className="mt-2 flex flex-wrap gap-x-6 gap-y-2">
                  {genders.map((gender) => (
                    <label
                      key={gender.id}
                      htmlFor={gender.id}
                      className="inline-flex items-center gap-2 text-sm text-gray-900 cursor-pointer"
                    >
                      <input
                        id={gender.id}
                        name="gender"
                        value={gender.id}
                        type="radio"
                        checked={gender.id === patient.gender}
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                        onChange={(e) => updateField("gender", e.target.value)}
                      />
                      {gender.title}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Contact */}
        <Section title="Contact">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("phone")} <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setIsConsentPopupVisible(true)}
                  className="text-xs font-medium text-amber-600 hover:text-amber-700 underline"
                >
                  Consent?
                </button>
              </div>
              <input
                type="text"
                className={inputCls}
                name="phone"
                maxLength={10}
                value={patient.phone}
                onChange={(e) => {
                  if (/^\+?[0-9]*$/.test(e.target.value)) {
                    updateField("phone", e.target.value);
                  }
                }}
              />
              {errors?.phone && (
                <span className="mt-1 text-xs text-red-600">{errors.phone}</span>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("email")}
              </label>
              <input
                type="text"
                className={inputCls}
                value={patient.email}
                onChange={(e) => updateField("email", e.target.value)}
              />
              {errors?.email && (
                <span className="mt-1 text-xs text-red-600">{errors.email}</span>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("Aadhar Number")}
              </label>
              <input
                type="text"
                className={inputCls}
                placeholder="12-digit Aadhar"
                name="aadharNumber"
                maxLength={12}
                value={patient.aadharNumber}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*$/.test(value)) {
                    updateField("aadharNumber", value);
                    setValidationErrors((prev: any) => ({
                      ...prev,
                      aadharNumber:
                        value.length > 0 && value.length !== 12
                          ? "Aadhar number must be exactly 12 digits"
                          : "",
                    }));
                  } else {
                    setValidationErrors((prev: any) => ({
                      ...prev,
                      aadharNumber: "Only digits are allowed",
                    }));
                  }
                }}
              />
              {(errors?.aadharNumber || validationErrors?.aadharNumber) && (
                <span className="mt-1 text-xs text-red-600">
                  {errors?.aadharNumber || validationErrors?.aadharNumber}
                </span>
              )}
            </div>
          </div>
        </Section>

        {/* Demographics */}
        <Section title="Demographics">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("birth_date")}
              </label>
              <input
                id="date"
                name="date"
                type="date"
                value={patient.birthDate}
                onChange={(e) => handleAge(e.target.value)}
                max={new Date().toISOString().split("T")[0]}
                className={inputCls}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("age")}
              </label>
              <input
                type="text"
                value={patient.age}
                onChange={(e) => {
                  if (/^\+?[0-9]*$/.test(e.target.value)) {
                    updateField("age", e.target.value);
                  }
                }}
                className={inputCls}
              />
              {errors?.age && (
                <span className="mt-1 text-xs text-red-600">{errors.age}</span>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("blood_group")}
              </label>
              <div className="mt-1">
                <Select
                  className="w-full"
                  showSearch
                  placeholder="Select blood group"
                  optionFilterProp="children"
                  onChange={onBloodGroupChange}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  value={patient.bloodGroup || undefined}
                  options={bloodGroups}
                  size="large"
                  allowClear
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t("referred_by_doctor")}
                </label>
                <button
                  type="button"
                  onClick={() => setReferredDoctorModalOpen(true)}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  + {t("add")}
                </button>
              </div>
              <Select
                className="w-full mt-1"
                placeholder="Select referred doctor"
                allowClear
                value={patient.referredBy || undefined}
                onChange={(value) => updateField("referredBy", value ?? null)}
                options={doctors.map((doctor: any) => ({
                  label: doctor.name,
                  value: doctor.id,
                }))}
                size="large"
              />
            </div>
          </div>
        </Section>

        {/* Guardian */}
        <Section title="Guardian">
          <div
            className={`rounded-lg p-4 ${
              isMinor
                ? "bg-amber-50 border border-amber-200"
                : "bg-gray-50 border border-gray-100"
            }`}
          >
            <div className="flex items-center gap-2 mb-3">
              {isMinor ? (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                  Minor — guardian recommended
                </span>
              ) : (
                <span className="text-xs text-gray-500">Optional</span>
              )}
            </div>
            {patient.guardian ? (
              <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-3 py-2">
                <div>
                  <span className="text-sm font-medium text-gray-900">
                    {patient.guardian.firstName} {patient.guardian.lastName}
                  </span>
                  <span className="ml-2 text-xs text-gray-500">
                    {patient.guardian.phone}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleGuardianClear}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ) : (
              <Select
                showSearch
                allowClear
                className="w-full"
                size="large"
                placeholder="Search guardian by phone (min 3 digits)"
                filterOption={false}
                loading={guardianSearching}
                onSearch={handleGuardianSearch}
                onSelect={(val: any) => handleGuardianSelect(val)}
                onClear={handleGuardianClear}
                notFoundContent={
                  guardianSearch.length >= 3
                    ? guardianSearching
                      ? "Searching..."
                      : "No patients found"
                    : "Type at least 3 digits to search"
                }
                options={guardianOptions.map((p: any) => ({
                  value: p.id,
                  label: `${p.firstName} ${p.lastName} — ${p.phone}`,
                }))}
              />
            )}
          </div>
        </Section>

        {/* Address & notes */}
        <Section title="Address & Notes">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("address")}
              </label>
              <textarea
                rows={3}
                className={`${inputCls} capitalize`}
                value={patient.address}
                onChange={(e) => updateField("address", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">
                {t("additional_details")}
              </label>
              <div className="mt-1">
                <Editor
                  editorState={commentsEditorState}
                  toolbarClassName="border border-gray-300 rounded-t-md"
                  wrapperClassName="h-auto"
                  editorClassName="min-h-[120px] border border-t-0 border-gray-300 rounded-b-md p-2"
                  onEditorStateChange={(editorState) =>
                    onEditorStateChange(
                      editorState,
                      "comments",
                      setCommentsEditorState
                    )
                  }
                  toolbar={{
                    options: ["inline", "list", "blockType"],
                    inline: {
                      options: ["bold", "italic", "underline"],
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </Section>
      </div>

      {isReferredDopctorModalOpen && (
        <Modal
          modalContent={
            <AddReferredDoctor closeModal={() => handleCloseModal()} />
          }
        />
      )}
      {isConsentPopupVisible && (
        <Modal
          title="Patient's Consent"
          onClose={() => setIsConsentPopupVisible(false)}
          modalContent={
            <div className="p-2">
              <span className="text-sm">
                If patient's record already exists in the system and if you
                don't have access to it, you will have to seek patient's consent
                in order to access his/her record. Please confirm his/her mobile
                number below to send him the consent form.
              </span>
              <div className="block mt-2">
                <label className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                  Phone
                </label>
                <input
                  type="text"
                  placeholder="Phone"
                  value={patient.phone}
                  className="block w-full mt-1 border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
                />
              </div>

              {validationErrors && validationErrors.phone && (
                <span className="text-sm text-red-500 leading-0">
                  {validationErrors.phone}
                </span>
              )}
              {isVisibleOtpField && (
                <div className="block mt-2">
                  <label className="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">
                    Enter OTP
                  </label>

                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    className="block w-full max-w-lg mt-1 border-gray-300 rounded-md shadow-sm form-control focus:border-gray-300 focus:ring focus:ring-gray-200 focus:ring-opacity-50 sm:text-sm"
                  />
                  {validationErrors && validationErrors?.otp && (
                    <p className="mt-2 text-sm text-red-600">
                      {validationErrors?.otp}
                    </p>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 mt-2 ">
                {isVisibleOtpField ? (
                  <button
                    onClick={() => handleVerifyOtp()}
                    type="submit"
                    className="px-3 py-2 ml-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dark"
                  >
                    Verify OTP
                  </button>
                ) : (
                  <button
                    onClick={() => handleSendOtp()}
                    className="px-3 py-2 text-white rounded-md bg-primary"
                  >
                    Send OTP
                  </button>
                )}
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};

export default BasicProfile;
